/**  @jsx jsx */
import { jsx } from '@emotion/core';
import { Button, CircularProgress, DialogActions, DialogContentText, Divider, Typography } from '@material-ui/core';
import { PayloadAction } from '@reduxjs/toolkit';
import { CustomDialogButtonContainer, CustomDialogContent } from 'components/Dialog/CustomDialog.styled';
import { ButtonVariantEnum } from 'enums/buttonVariant';
import { DialogVariantEnum } from 'enums/dialogVariant';
import { Component, Fragment } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { subscribeActionAfter } from 'redux-subscribe-action';
import { AppDispatch, AppState } from 'store';
import { dialogClose, dialogOpen } from 'store/dialog/dialog.actions';
import { DialogOpenPayloadJobs, IDialogOpenPayload } from 'store/dialog/dialog.interface';
import { getSimilarOperationIds, performOperation } from 'store/jobs/jobs.actions';
import { JobsActionTypes } from 'store/jobs/jobs.actions.types';
import { getSimilarIdsSelector } from 'store/jobs/jobs.selectors';
import { getJobActionSpinnerSelector, getSecondJobActionSpinnerSelector } from 'store/spinner/spinner.selectors';
import { getRequiredJobStatuses } from 'utils/getRequiredJobStatuses';
import { ErrorMessageDetailsDTO, JobOperation, MassOperationRequest, XTMJobStatus } from 'utils/restApplicationClient';

export interface IJobActionProps {
  variant?: DialogVariantEnum;
  action?: DialogVariantEnum;
  onClose: ((response: ButtonVariantEnum, variant?: DialogVariantEnum) => void) | undefined;
  statuses?: Array<XTMJobStatus>;
  projectId: string;
  projectLanguages: Array<string>;
  jobs: DialogOpenPayloadJobs;
  errorMessage: string;
  errorMessagesDetails?: ErrorMessageDetailsDTO[];
}

interface IDispatchProps {
  dialogOpen: (payload: IDialogOpenPayload) => AppDispatch;
  dialogClose: () => AppDispatch;
  getSimilarOperationIds: (payload: MassOperationRequest) => AppDispatch;
  performOperation: (payload: MassOperationRequest) => AppDispatch;
}

interface IStateProps {
  jobActionSpinner: boolean;
  secondJobActionSpinner: boolean;
  similarIds: string[];
}

type PropType = IJobActionProps & WithTranslation & IStateProps & IDispatchProps;

class JobAction extends Component<PropType> {
  private unsubscribe: () => void = () => ({});
  private getVariantText = (variant: IJobActionProps['variant']): string | undefined => {
    const { jobs } = this.props;
    const multiple = jobs?.length > 1;

    switch (variant) {
      case DialogVariantEnum.IMPORT: {
        return `modal.inTranslationQueueImportAction${multiple ? 's' : ''}`;
      }
      case DialogVariantEnum.DELETE: {
        return `modal.inTranslationQueueRemoveAction${multiple ? 's' : ''}`;
      }
      case DialogVariantEnum.CANCEL: {
        return `modal.inTranslationQueueCancelAction${multiple ? 's' : ''}`;
      }
      case DialogVariantEnum.SIMILAR_OPERATION: {
        return `modal.similarOperation`;
      }
      case DialogVariantEnum.SEND: {
        return `modal.inTranslationQueueSendAction${multiple ? 's' : ''}`;
      }
      default:
        return undefined;
    }
  };

  private handleClose = () => (): void => {
    const { dialogClose } = this.props;
    dialogClose();
  };

  private handleSubmit = (withSimilarIds: boolean) => (): void => {
    const { variant, getSimilarOperationIds, jobs, similarIds, dialogClose, dialogOpen, performOperation } = this.props;
    const requiredStatuses = getRequiredJobStatuses(variant);
    const jobIds = jobs.filter(({ status }) => requiredStatuses.includes(status as XTMJobStatus)).map(({ id }) => id);

    if (variant !== DialogVariantEnum.SIMILAR_OPERATION) {
      getSimilarOperationIds({
        jobsIds: jobIds,
        operation: this.props.action as JobOperation,
      });

      this.unsubscribe = subscribeActionAfter(JobsActionTypes.GetSimilarOperationIdsSuccess, (action) => {
        if ((action as PayloadAction<{ data: Array<string> }>).payload.data.length) {
          dialogClose();
          dialogOpen({
            variant: DialogVariantEnum.SIMILAR_OPERATION,
            action: variant,
            projectInfo: {
              jobs,
            },
          });
        } else {
          performOperation({ jobsIds: jobIds, operation: this.props.action as JobOperation });
        }
      });
    } else {
      const jobsIds = withSimilarIds ? [...jobIds, ...similarIds] : jobIds;

      performOperation({ jobsIds, operation: this.props.action as JobOperation });
    }
  };

  render(): JSX.Element {
    const { t, variant, similarIds, jobActionSpinner, secondJobActionSpinner } = this.props;

    return (
      <Fragment>
        <CustomDialogContent>
          <DialogContentText>
            <Typography component="span" color="textPrimary">
              {t(this.getVariantText(variant) || '', {
                count: similarIds.length,
              })}
            </Typography>
          </DialogContentText>
        </CustomDialogContent>
        <Divider />
        <DialogActions>
          <CustomDialogButtonContainer>
            <Button
              variant="contained"
              color="secondary"
              onClick={this.handleSubmit(true)}
              disabled={variant === DialogVariantEnum.SIMILAR_OPERATION ? secondJobActionSpinner : jobActionSpinner}
            >
              {variant === DialogVariantEnum.SIMILAR_OPERATION &&
                (secondJobActionSpinner ? <CircularProgress size="24px" color="secondary" /> : t('modal.yes'))}
              {variant !== DialogVariantEnum.SIMILAR_OPERATION &&
                (jobActionSpinner ? <CircularProgress size="24px" color="secondary" /> : t('modal.ok'))}
            </Button>
            <Button
              variant="outlined"
              onClick={variant !== DialogVariantEnum.SIMILAR_OPERATION ? this.handleClose() : this.handleSubmit(false)}
              disabled={variant === DialogVariantEnum.SIMILAR_OPERATION ? secondJobActionSpinner : jobActionSpinner}
            >
              {variant === DialogVariantEnum.SIMILAR_OPERATION &&
                (secondJobActionSpinner ? <CircularProgress size="24px" color="secondary" /> : t('modal.no'))}
              {variant !== DialogVariantEnum.SIMILAR_OPERATION && t('common.cancel')}
            </Button>
          </CustomDialogButtonContainer>
        </DialogActions>
      </Fragment>
    );
  }

  componentWillUnmount(): void {
    this.unsubscribe();
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  dialogOpen: (payload: IDialogOpenPayload): AppDispatch => dispatch(dialogOpen(payload)),
  dialogClose: (): AppDispatch => dispatch(dialogClose()),
  getSimilarOperationIds: (payload: MassOperationRequest): AppDispatch => dispatch(getSimilarOperationIds(payload)),
  performOperation: (payload: MassOperationRequest): AppDispatch => dispatch(performOperation(payload)),
});

const mapStateToProps = (state: AppState): IStateProps => ({
  jobActionSpinner: getJobActionSpinnerSelector(state),
  secondJobActionSpinner: getSecondJobActionSpinnerSelector(state),
  similarIds: getSimilarIdsSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(JobAction));
